import React, { useEffect, useState } from "react";
import LayoutBuyerSeller from "../../components/layout/LayoutBuyerSeller";
import settingsIcon from "../../assets/icon/settings.svg";
import { LogOut } from "lucide-react";
import { useAuth } from "../../context/authContext";
import apiService from "../../actions/apiService";

const MyProfile = () => {
  const { logout, user } = useAuth();

  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [logoutLoading, setLogoutLoading] = useState(false);
  const [bankForm, setBankForm] = useState({
    preferred_payout_method: "bank_transfer",
    account_holder_name: "",
    bank_name: "",
    iban: "",
    swift_bic: "",
  });

  const [sellerStats, setSellerStats] = useState({
    totalSales: 0,
    totalSoldValue: 0,
    sellerRating: 0,
  });
  const [statsLoading, setStatsLoading] = useState(false);

  const handleBankChange = (e) => {
    const { name, value } = e.target;
    setBankForm((prev) => ({ ...prev, [name]: value }));
  };

  // Helpers to mask values when not editing
  const maskAll = (value) => {
    const str = String(value || "");
    if (!str) return "";
    return "*".repeat(Math.min(str.length, 12));
  };

  const maskKeepLast4 = (value) => {
    const str = String(value || "");
    if (str.length <= 4) return "*".repeat(str.length);
    const visible = str.slice(-4);
    return "*".repeat(Math.min(str.length - 4, 12)) + visible;
  };

  const handleSaveBank = async (e) => {
    e.preventDefault();
    if (!user?.id) return;
    setSaving(true);
    setSaveError(null);
    const result = await apiService.put(`/bank-accounts/${user.id}`, bankForm);
    if (!result.success) {
      setSaveError(result.error || "Failed to save bank details");
    } else {
      setIsEditing(false);
    }
    setSaving(false);
  };

  // Load existing bank details on mount/user change
  useEffect(() => {
    const loadBank = async () => {
      if (!user?.id) return;
      const res = await apiService.get(`/bank-accounts/${user.id}`);
      if (res?.success && res.data) {
        const r = res.data;
        setBankForm({
          preferred_payout_method: r.preferred_payout_method || "bank_transfer",
          account_holder_name: r.account_holder_name || "",
          bank_name: r.bank_name || "",
          account_number: r.account_number || "",
          iban: r.iban || "",
          swift_bic: r.swift_bic || "",
          account_type: r.account_type || "checking",
          country_code: r.country_code || "US",
        });
      }
    };
    loadBank();
  }, [user?.id]);

  useEffect(() => {
    const fetchSellerStats = async () => {
      if (user?.role !== "seller") return;
      setStatsLoading(true);
      try {
        const res = await apiService.get("/transactions/seller-stats");
        if (res?.success && res.data) {
          setSellerStats({
            totalSales: res.data.totalSales || 0,
            totalSoldValue: res.data.totalSoldValue || 0,
            sellerRating:
              typeof res.data.sellerRating === "number"
                ? res.data.sellerRating
                : 0,
          });
        } else {
          setSellerStats({
            totalSales: 0,
            totalSoldValue: 0,
            sellerRating: 0,
          });
        }
      } catch (_) {
        setSellerStats({
          totalSales: 0,
          totalSoldValue: 0,
          sellerRating: 0,
        });
      } finally {
        setStatsLoading(false);
      }
    };

    fetchSellerStats();
  }, [user?.id, user?.role]);

  const handleLogout = async () => {
    try {
      setLogoutLoading(true);
      await logout();
    } finally {
      setLogoutLoading(false);
    }
  };

  // Format join date as "Month YYYY"
  const formattedJoined = (() => {
    const raw = user?.created_at || user?.createdAt || user?.created_on;
    if (!raw) return "";
    try {
      const d = new Date(raw);
      if (isNaN(d.getTime())) return "";
      return d.toLocaleString(undefined, { month: "long", year: "numeric" });
    } catch (_) {
      return "";
    }
  })();

  return (
    <LayoutBuyerSeller>
      <div className="absolute lg:hidden top-0 left-0 h-72 w-full bg-blue-gradient"></div>
      <div className="max-w-[842px] mx-auto">
        <div className="mb-8 relative z-[10]">
          <div className="flex items-center justify-between">
            <div className="px-4 md:px-0">
              <h2 className="text-page-title md:text-page-title-d text-white lg:text-[#1E1E1E] font-rubik">
                My profile
              </h2>
              <p className="text-small-price opacity-50 mt-4 md:text-small-price text-white lg:text-[#1E1E1E] !font-normal">
                Your public seller profile and business information.
              </p>
            </div>
            <button className="hidden lg:inline-flex items-center gap-4 px-10 py-[12.5px] bg-blue-gradient text-white rounded-[8px] text-sm">
              <div>
                <img
                  src={settingsIcon}
                  alt="settingsIcon"
                  className="w-[24px] h-[24px]"
                />
              </div>
              <div>Profile settings</div>
            </button>
          </div>
        </div>

        <div className="z-[10] w-full bg-[#F5F8FF] lg:bg-transparent p-5 lg:p-0 relative rounded-tl-[25px] lg:rounded-tl-[12px] rounded-tr-[25px] lg:rounded-tr-[12px] rounded-br-[0px] lg:rounded-br-[12px] rounded-bl-[0px] lg:rounded-bl-[12px] ">
          <button className="inline-flex mb-8 w-full justify-center lg:hidden items-center gap-4 px-10 py-[12.5px] bg-blue-gradient text-white rounded-[8px] text-sm">
            <div>
              <img
                src={settingsIcon}
                alt="settingsIcon"
                className="w-[24px] h-[24px]"
              />
            </div>
            <div>Profile settings</div>
          </button>

          <section className="bg-white border border-[#DCEDFF] rounded-[12px] p-5">
            <div className="flex items-start justify-between">
              <div className="w-full">
                <div className="flex items-start justify-between">
                  <h3 className="text-section-title md:text-section-title-d text-esdarkGray font-semibold">
                    Public profile
                  </h3>
                  <span className="px-[10px] flex lg:hidden items-center gap-2 justify-center py-1 rounded-[50px] border border-[#BBF7D0] bg-[#DCFCE7] text-[#16653F] text-tag-d font-medium">
                    <div>
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="12"
                        height="13"
                        viewBox="0 0 12 13"
                        fill="none"
                      >
                        <path
                          d="M4.5 6.5L5.5 7.5L7.75 5.25M3.66688 2.40935C4.0688 2.37727 4.45036 2.21923 4.75723 1.95771C5.47336 1.34743 6.52664 1.34743 7.24277 1.95771C7.54964 2.21923 7.9312 2.37727 8.33312 2.40935C9.27103 2.48419 10.0158 3.22897 10.0907 4.16688C10.1227 4.5688 10.2808 4.95036 10.5423 5.25723C11.1526 5.97336 11.1526 7.02664 10.5423 7.74277C10.2808 8.04964 10.1227 8.4312 10.0907 8.83312C10.0158 9.77103 9.27103 10.5158 8.33312 10.5907C7.9312 10.6227 7.54964 10.7808 7.24277 11.0423C6.52664 11.6526 5.47336 11.6526 4.75723 11.0423C4.45036 10.7808 4.0688 10.6227 3.66688 10.5907C2.72897 10.5158 1.98419 9.77103 1.90935 8.83312C1.87727 8.4312 1.71923 8.04964 1.45771 7.74277C0.84743 7.02664 0.84743 5.97336 1.45771 5.25723C1.71923 4.95036 1.87727 4.5688 1.90935 4.16688C1.98419 3.22897 2.72897 2.48419 3.66688 2.40935Z"
                          stroke="#003366"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </div>
                    <div>Verified</div>
                  </span>
                </div>
                <p className="text-body-d text-esDarkBlue opacity-50 mt-2">
                  How other users view your profile.
                </p>
              </div>
            </div>
            <div className="mt-8 flex items-center gap-4">
              {user?.profile_image_url ? (
                <img
                  src={
                    process.env.REACT_APP_IMAGE_URL + user?.profile_image_url
                  }
                  alt="avatar"
                  className="w-16 h-16 rounded-full object-cover bg-gray-100"
                />
              ) : (
                <span className="text-slate-600 rounded-full object-cover bg-slate-200  w-16 h-16 text-xl font-semibold flex items-center justify-center">
                  {user?.firstName?.charAt(0) + user?.lastName?.charAt(0)}
                </span>
              )}
              <div className="">
                <div className="flex items-center gap-4">
                  <div className="text-small-price-d opacity-75 font-medium text-esDarkBlue">
                    {user?.firstName && user?.lastName && (
                      <span className="text-esDarkBlue">
                        {user?.firstName} {user?.lastName}
                      </span>
                    )}
                    {!user?.firstName && !user?.lastName && (
                      <span className="text-esDarkBlue">N/A</span>
                    )}
                  </div>
                  {user?.email_verified && (
                    <span className="px-[10px] hidden md:flex items-center gap-2 justify-center py-1 rounded-[50px] border border-[#BBF7D0] bg-[#DCFCE7] text-[#16653F] text-tag-d font-medium">
                      <div>
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="12"
                          height="13"
                          viewBox="0 0 12 13"
                          fill="none"
                        >
                          <path
                            d="M4.5 6.5L5.5 7.5L7.75 5.25M3.66688 2.40935C4.0688 2.37727 4.45036 2.21923 4.75723 1.95771C5.47336 1.34743 6.52664 1.34743 7.24277 1.95771C7.54964 2.21923 7.9312 2.37727 8.33312 2.40935C9.27103 2.48419 10.0158 3.22897 10.0907 4.16688C10.1227 4.5688 10.2808 4.95036 10.5423 5.25723C11.1526 5.97336 11.1526 7.02664 10.5423 7.74277C10.2808 8.04964 10.1227 8.4312 10.0907 8.83312C10.0158 9.77103 9.27103 10.5158 8.33312 10.5907C7.9312 10.6227 7.54964 10.7808 7.24277 11.0423C6.52664 11.6526 5.47336 11.6526 4.75723 11.0423C4.45036 10.7808 4.0688 10.6227 3.66688 10.5907C2.72897 10.5158 1.98419 9.77103 1.90935 8.83312C1.87727 8.4312 1.71923 8.04964 1.45771 7.74277C0.84743 7.02664 0.84743 5.97336 1.45771 5.25723C1.71923 4.95036 1.87727 4.5688 1.90935 4.16688C1.98419 3.22897 2.72897 2.48419 3.66688 2.40935Z"
                            stroke="#003366"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </div>
                      <div>Verified</div>
                    </span>
                  )}
                </div>
                <p className="opacity-50 text-[#1E1E1E] my-2 text-[12px]">
                  {user?.email || "N/A"}
                </p>
                <p className="opacity-50 text-[#1E1E1E] text-[12px]">
                  {formattedJoined ? (
                    <span className="text-esDarkBlue">
                      Joined {formattedJoined}
                    </span>
                  ) : (
                    <span className="text-esDarkBlue">N/A</span>
                  )}
                </p>
              </div>
            </div>
            <button
              onClick={handleLogout}
              disabled={logoutLoading}
              className={`inline-flex mt-8 w-full justify-center items-center gap-4 px-10 py-[12.5px] rounded-[8px] text-[14px] ${logoutLoading
                  ? "bg-blue-gradient cursor-not-allowed text-white"
                  : "bg-blue-gradient text-white"
                }`}
            >
              {logoutLoading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  <span>Logging out...</span>
                </>
              ) : (
                <>
                  <LogOut className="w-[24px] h-[24px]" />
                  <span>Logout</span>
                </>
              )}
            </button>
          </section>

          <section className="bg-white my-8 border border-[#DCEDFF] rounded-[12px] p-5">
            <h3 className="text-section-title md:text-section-title-d text-esdarkGray font-semibold">
              Contact information
            </h3>
            <p className="text-body-d text-esDarkBlue opacity-50 mt-2">
              Manage visibility of your contact details to potential buyers.
            </p>
            <div className="mt-8 space-y-4 text-esDarkBlue">
              <div className="flex items-center gap-3">
                <span>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                  >
                    <g opacity="0.5">
                      <path
                        d="M21.5 18L14.8571 12M9.14286 12L2.50003 18M2 7L10.1649 12.7154C10.8261 13.1783 11.1567 13.4097 11.5163 13.4993C11.8339 13.5785 12.1661 13.5785 12.4837 13.4993C12.8433 13.4097 13.1739 13.1783 13.8351 12.7154L22 7M6.8 20H17.2C18.8802 20 19.7202 20 20.362 19.673C20.9265 19.3854 21.3854 18.9265 21.673 18.362C22 17.7202 22 16.8802 22 15.2V8.8C22 7.11984 22 6.27976 21.673 5.63803C21.3854 5.07354 20.9265 4.6146 20.362 4.32698C19.7202 4 18.8802 4 17.2 4H6.8C5.11984 4 4.27976 4 3.63803 4.32698C3.07354 4.6146 2.6146 5.07354 2.32698 5.63803C2 6.27976 2 7.11984 2 8.8V15.2C2 16.8802 2 17.7202 2.32698 18.362C2.6146 18.9265 3.07354 19.3854 3.63803 19.673C4.27976 20 5.11984 20 6.8 20Z"
                        stroke="#1E1E1E"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </g>
                  </svg>
                </span>
                <span className="text-body-d text-esDarkBlue">
                  {user?.email && (
                    <span className="text-esDarkBlue">{user?.email}</span>
                  )}
                  {!user?.email && <span className="text-esDarkBlue">N/A</span>}
                </span>
              </div>
              <div className="flex items-center gap-3">
                <span>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                  >
                    <g opacity="0.5">
                      <path
                        d="M8.38028 8.85335C9.07627 10.303 10.0251 11.6616 11.2266 12.8632C12.4282 14.0648 13.7869 15.0136 15.2365 15.7096C15.3612 15.7694 15.4235 15.7994 15.5024 15.8224C15.7828 15.9041 16.127 15.8454 16.3644 15.6754C16.4313 15.6275 16.4884 15.5704 16.6027 15.4561C16.9523 15.1064 17.1271 14.9316 17.3029 14.8174C17.9658 14.3864 18.8204 14.3864 19.4833 14.8174C19.6591 14.9316 19.8339 15.1064 20.1835 15.4561L20.3783 15.6509C20.9098 16.1824 21.1755 16.4481 21.3198 16.7335C21.6069 17.301 21.6069 17.9713 21.3198 18.5389C21.1755 18.8242 20.9098 19.09 20.3783 19.6214L20.2207 19.779C19.6911 20.3087 19.4263 20.5735 19.0662 20.7757C18.6667 21.0001 18.0462 21.1615 17.588 21.1601C17.1751 21.1589 16.8928 21.0788 16.3284 20.9186C13.295 20.0576 10.4326 18.4332 8.04466 16.0452C5.65668 13.6572 4.03221 10.7948 3.17124 7.76144C3.01103 7.19699 2.93092 6.91477 2.9297 6.50182C2.92833 6.0436 3.08969 5.42311 3.31411 5.0236C3.51636 4.66357 3.78117 4.39876 4.3108 3.86913L4.46843 3.7115C4.99987 3.18006 5.2656 2.91433 5.55098 2.76999C6.11854 2.48292 6.7888 2.48292 7.35636 2.76999C7.64174 2.91433 7.90747 3.18006 8.43891 3.7115L8.63378 3.90637C8.98338 4.25597 9.15819 4.43078 9.27247 4.60655C9.70347 5.26945 9.70347 6.12403 9.27247 6.78692C9.15819 6.96269 8.98338 7.1375 8.63378 7.4871C8.51947 7.60142 8.46231 7.65857 8.41447 7.72538C8.24446 7.96281 8.18576 8.30707 8.26748 8.58743C8.29048 8.66632 8.32041 8.72866 8.38028 8.85335Z"
                        stroke="#1E1E1E"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </g>
                  </svg>
                </span>
                <span className="text-body-d text-esDarkBlue">
                  {user?.phone && (
                    <span className="text-esDarkBlue">{user?.phone}</span>
                  )}
                  {!user?.phone && <span className="text-esDarkBlue">N/A</span>}
                </span>
              </div>
            </div>
          </section>

          {user?.role === "seller" && (
            <>
              <section className="bg-white my-8 border border-[#DCEDFF] rounded-[12px] p-5">
                <h3 className="text-section-title md:text-section-title-d text-esdarkGray font-semibold">
                  Seller statistics
                </h3>
                <p className="text-body-d text-esDarkBlue opacity-50 mt-2">
                  Your performance metrics and seller reputation.
                </p>
                <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="flex-col flex gap-1">
                    <div className="text-esDarkBlue text-caption opacity-50">
                      Total sales
                    </div>
                    <div className="text-esDarkBlue text-small-price-d font-bold">
                      {statsLoading
                        ? "0"
                        : sellerStats.totalSales.toLocaleString()}
                    </div>
                  </div>
                  <div className="flex-col flex gap-1">
                    <div className="text-esDarkBlue text-caption opacity-50">
                      Seller’s rating
                    </div>
                    <div className="text-esDarkBlue flex items-center gap-1 text-small-price-d font-bold">
                      <div>
                        {statsLoading
                          ? "0.0"
                          : Number(sellerStats.sellerRating || 0).toFixed(1)}
                      </div>{" "}
                      <span className="text-esblue text-tag font-medium">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="20"
                          height="18"
                          viewBox="0 0 20 18"
                          fill="none"
                        >
                          <path
                            d="M10 0.5L12.1329 7.06434H19.035L13.4511 11.1213L15.584 17.6857L10 13.6287L4.41604 17.6857L6.54892 11.1213L0.964963 7.06434H7.86712L10 0.5Z"
                            fill="#FFF155"
                          />
                        </svg>
                      </span>
                    </div>
                  </div>
                  <div className="flex-col flex gap-1">
                    <div className="text-esDarkBlue text-caption opacity-50">
                      Total sold
                    </div>
                    <div className="text-esDarkBlue text-small-price-d font-bold">
                      {statsLoading
                        ? "0 €"
                        : `${sellerStats.totalSoldValue.toLocaleString()} €`}
                    </div>
                  </div>
                </div>
              </section>

              <section className="bg-white hidden my-8 border border-[#DCEDFF] rounded-[12px] p-5">
                <h3 className="text-section-title md:text-section-title-d text-esdarkGray font-semibold">
                  Business information
                </h3>
                <p className="text-body-d text-esDarkBlue opacity-50 mt-2">
                  Company information and IDs.
                </p>
                <div className="mt-8 grid  grid-cols-1 md:grid-cols-3 gap-6 text-esDarkBlue">
                  <div>
                    <div className="text-body-d font-semibold text-esDarkBlue">
                      Company name
                    </div>
                    <div className="text-body-d">Luxury Assets Ltd.</div>
                  </div>
                  <div>
                    <div className="text-body-d font-semibold text-esDarkBlue">
                      Company ID
                    </div>
                    <div className="text-body-d">FR0192837465</div>
                  </div>
                  <div>
                    <div className="text-body-d font-semibold text-esDarkBlue">
                      VAT number
                    </div>
                    <div className="text-body-d">FR21928374659</div>
                  </div>
                </div>
              </section>
            </>
          )}

          <section className="bg-white border border-[#DCEDFF] rounded-[12px] p-5">
            <div className="flex flex-col gap-8 md:flex-row items-center justify-between">
              <div className="w-full">
                <h3 className="text-section-title md:text-section-title-d text-esdarkGray font-semibold">
                  Bank account details
                </h3>
                <p className="text-body-d text-esDarkBlue opacity-50 mt-2">
                  Your payout information (admin only).
                </p>
              </div>
              <button
                onClick={() => setIsEditing((prev) => !prev)}
                className="inline-flex md:w-auto w-full justify-center items-center gap-4 px-10 py-2 bg-blue-gradient text-white rounded-[8px]"
              >
                <div>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="22"
                    height="22"
                    viewBox="0 0 22 22"
                    fill="none"
                  >
                    <path
                      d="M16.5 9.0003L12.5 5.0003M1 20.5003L4.38437 20.1243C4.79786 20.0783 5.0046 20.0553 5.19785 19.9928C5.36929 19.9373 5.53245 19.8589 5.68289 19.7597C5.85245 19.6479 5.99955 19.5008 6.29373 19.2066L19.5 6.0003C20.6046 4.89573 20.6046 3.10487 19.5 2.0003C18.3955 0.895734 16.6046 0.895732 15.5 2.0003L2.29373 15.2066C1.99955 15.5008 1.85246 15.6478 1.74064 15.8174C1.64143 15.9679 1.56301 16.131 1.50751 16.3025C1.44496 16.4957 1.42198 16.7024 1.37604 17.1159L1 20.5003Z"
                      stroke="white"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
                <div className="text-nav-d">
                  {isEditing ? "Cancel" : "Edit"}
                </div>
              </button>
            </div>
            {saveError && (
              <div className="text-red-500 text-sm mt-4">{saveError}</div>
            )}
            <form
              onSubmit={handleSaveBank}
              className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4"
            >
              <div className="md:col-span-2 mb-4">
                <label className="block text-caption opacity-50 text-esDarkBlue mb-2">
                  Preferred payout method
                </label>
                <div className="relative">
                  <select
                    name="preferred_payout_method"
                    value={bankForm.preferred_payout_method}
                    onChange={handleBankChange}
                    disabled={!isEditing}
                    required
                    className="w-full rounded-[8px] border border-gray-200 px-3 py-3 pr-8 appearance-none bg-white cursor-pointer disabled:opacity-60"
                    placeholder="***********"
                  >
                    <option value="bank_transfer">Bank Transfer</option>
                  </select>
                  <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                    <svg
                      className="w-4 h-4 text-gray-400"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </div>
                </div>
              </div>
              <div>
                <label className="block text-caption text-esDarkBlue opacity-50 mb-2">
                  Account holder name
                </label>
                <input
                  name="account_holder_name"
                  value={
                    isEditing
                      ? bankForm.account_holder_name
                      : maskAll(bankForm.account_holder_name)
                  }
                  onChange={handleBankChange}
                  disabled={!isEditing}
                  required
                  className="w-full py-3 rounded-[8px] border border-gray-200 px-3 disabled:opacity-60"
                  placeholder="***********"
                />
              </div>
              <div>
                <label className="block text-caption text-esDarkBlue opacity-50 mb-2">
                  Bank name
                </label>
                <input
                  name="bank_name"
                  value={
                    isEditing ? bankForm.bank_name : maskAll(bankForm.bank_name)
                  }
                  onChange={handleBankChange}
                  disabled={!isEditing}
                  required
                  className="w-full py-3 rounded-[8px] border border-gray-200 px-3 disabled:opacity-60"
                  placeholder="***********"
                />
              </div>
              <div>
                <label className="block text-caption text-esDarkBlue opacity-50 mb-2">
                  IBAN
                </label>
                <input
                  name="iban"
                  value={
                    isEditing ? bankForm.iban : maskKeepLast4(bankForm.iban)
                  }
                  onChange={handleBankChange}
                  disabled={!isEditing}
                  required
                  className="w-full py-3 rounded-[8px] border border-gray-200 px-3 disabled:opacity-60"
                  placeholder="**** **** *** *** **"
                />
              </div>
              <div>
                <label className="block text-caption text-esDarkBlue opacity-50 mb-2">
                  SWIFT/BIC code
                </label>
                <input
                  name="swift_bic"
                  value={
                    isEditing
                      ? bankForm.swift_bic
                      : maskKeepLast4(bankForm.swift_bic)
                  }
                  onChange={handleBankChange}
                  required
                  disabled={!isEditing}
                  className="w-full py-3 rounded-[8px] border border-gray-200 px-3 disabled:opacity-60"
                  placeholder="********"
                />
              </div>
              {isEditing && (
                <div className="md:col-span-2">
                  <button
                    type="submit"
                    disabled={saving}
                    className="inline-flex w-full md:w-auto justify-center items-center gap-4 px-10 py-2 bg-blue-gradient text-white rounded-[8px]"
                  >
                    {saving ? "Saving..." : "Save bank details"}
                  </button>
                </div>
              )}
            </form>
          </section>
        </div>
      </div>
    </LayoutBuyerSeller>
  );
};

export default MyProfile;
